import { FC, useState } from "react";
import { IOrder } from "../../../model/IOrder";
import priceFormatter from "../../../utils/priceFormatter";
import {
  feedPrice,
  freightDiscount,
  partPrice,
  subtotal,
  vendorPrice,
} from "../../../utils/orderTotals";
import { DetailPanel, DetailRow } from "./DetailPanel";
import { useAccess } from "../../../hooks/useAccess";
import { icons } from "../../../icons/IconSet";
import Button from "../../form/Button";
import RefundSection from "../refunds/RefundSection";
import ResendEmailButtons from "../emails/ResendEmailButtons";
import OrderEditModal from "../edit/OrderEditModal";

const money = (value: number | null | undefined) =>
  value === null || value === undefined ? null : priceFormatter(value);

const stamp = (value: string | null) => {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? value
    : `${date.toLocaleDateString('en-CA')} ${date.toLocaleTimeString('en-GB')}`;
};

const OrderMain: FC<{ order: IOrder }> = ({ order }) => {
  // Adding and editing refunds is admin only, matching the endpoints.
  const { hasRole } = useAccess();
  const isAdmin = hasRole('ROLE_ADMIN');
  // Editing the order is open to agents too; resending its emails is not.
  const canEditOrder = isAdmin || hasRole('ROLE_AGENT');
  const [isEditing, setIsEditing] = useState<boolean>(false);

  return (
  <div className="flex flex-col gap-4">
    <DetailPanel
      title="Main Info"
      actions={(
        <div className="flex flex-wrap items-center gap-2">
          {canEditOrder && (
            <Button
              isNotFullWidth
              size="small"
              variant="outline"
              className="flex items-center gap-1 whitespace-nowrap"
              onClick={() => setIsEditing(true)}
            >
              {icons('edit-record', { width: '14px' })}
              Edit Order
            </Button>
          )}
          {isAdmin && <ResendEmailButtons order={order} />}
        </div>
      )}
    >
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-x-8">
        <div>
          <DetailRow label="Order#" value={order.orderId} />
          <DetailRow label="IP Address" value={order.ip} />
          <DetailRow label="Website" value={order.fromSite} />
          {/*
            A plain box for the status. The old page drove it from Department /
            Category / Subcategory selects that filled this in; those come later.
          */}
          <DetailRow label="Status" value={order.status} />
          <DetailRow label="Created" value={stamp(order.timeCreate)} />
          <DetailRow label="Updated" value={stamp(order.timeUpdate)} />
          <DetailRow label="Scope" value={order.fromSiteCleaned} />
          <DetailRow label="Campaign" value={order.campaignId} />
          <DetailRow label="Referral" value={order.entryFrom} />
          <DetailRow label="Invoice" value={order.invoice} />
        </div>

        <div>
          <DetailRow label="Part Price" value={money(partPrice(order))} />
          <DetailRow
            label="Coupon Discount"
            value={money(order.discountAmount)
              ? `${money(order.discountAmount)}${order.couponCode ? ` (Coupon Code: ${order.couponCode})` : ''}`
              : null}
          />
          <DetailRow label="Freight Discount" value={money(freightDiscount(order))} />
          <DetailRow label="Subtotal" value={money(subtotal(order))} />
          <DetailRow label="Shipping" value={money(order.shipping)} />
          <DetailRow label="Shipping Protection Fee" value={money(order.shippingProtectionFee)} />
          <DetailRow label="Handling" value={money(order.handling)} />
          <DetailRow label="Taxes" value={money(order.taxes)} />
          <DetailRow label="Core" value={money(order.corePrice)} />
          <DetailRow
            label="Total"
            value={<span className="font-semibold">{money(order.totalPrice)}</span>}
          />
          <DetailRow label="vPrice" value={money(vendorPrice(order))} />
          <DetailRow label="fPrice" value={money(feedPrice(order))} />
          <DetailRow label="Shipping To Billing Dist" value={order.billingShippingDistance} />
          <DetailRow label="Nearby" value={order.nearby} />
          <DetailRow label="Distance" value={order.distance} />
        </div>
      </div>

      <div className="mt-4">
        <div className="text-text_pale mb-1">Comments</div>
        {/* Line breaks preserved - comments are typed with them. */}
        <div className="whitespace-pre-line">
          {order.notes || <span className="text-gray-300">—</span>}
        </div>
      </div>
    </DetailPanel>

    <RefundSection order={order} vendor={false} canEdit={isAdmin} />

    <RefundSection order={order} vendor={true} canEdit={isAdmin} />

    {isEditing && <OrderEditModal order={order} onClose={() => setIsEditing(false)} />}
  </div>
  );
};

export default OrderMain;
